import { useState } from "react";
import { gql, useQuery, useLazyQuery, useMutation } from "@apollo/client";
import { useSelector } from "react-redux";
import {
  Container,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import CustomCircularProgress from "./UiComponents/CustomCircularProgress";
import { toast } from "react-toastify";

const GET_TEMPLATE_NAMES = gql`
  query EmailTemplateNames {
    emailTemplateNames
  }
`;

const PREVIEW_TEMPLATE = gql`
  query PreviewEmailTemplate($templateName: String!) {
    previewEmailTemplate(templateName: $templateName) {
      name
      html
    }
  }
`;

const SEND_TEST_EMAIL = gql`
  mutation SendTestEmail($toEmail: String) {
    sendTestEmail(toEmail: $toEmail) {
      success
      statusCode
      message
      toEmail
    }
  }
`;

export const TestEmail = () => {
  const currentUser = useSelector((state) => state.auth);

  //Pre-fill with the signed-in user's email (MSAL UPN) when available
  const defaultEmail =
    currentUser &&
    currentUser.jwtIdToken &&
    currentUser.jwtIdToken.account &&
    currentUser.jwtIdToken.account.username
      ? currentUser.jwtIdToken.account.username
      : "";

  const [toEmail, setToEmail] = useState(defaultEmail);
  const [result, setResult] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState("");

  const templateNamesQuery = useQuery(GET_TEMPLATE_NAMES);
  const [previewTemplate, previewTemplateResult] = useLazyQuery(
    PREVIEW_TEMPLATE,
    { fetchPolicy: "network-only" }
  );
  const [sendTestEmail, sendTestEmailResult] = useMutation(SEND_TEST_EMAIL);

  const templateNames =
    (templateNamesQuery.data && templateNamesQuery.data.emailTemplateNames) ||
    [];

  const previewHtml =
    (previewTemplateResult.data &&
      previewTemplateResult.data.previewEmailTemplate &&
      previewTemplateResult.data.previewEmailTemplate.html) ||
    "";

  const toastOptions = {
    position: "top-right",
    autoClose: 5000,
    closeOnClick: true,
    hideProgressBar: false,
  };

  const onSelectTemplate = (name) => {
    setSelectedTemplate(name);
    if (name) {
      previewTemplate({ variables: { templateName: name } });
    }
  };

  const onSend = () => {
    setResult(null);
    sendTestEmail({ variables: { toEmail: toEmail.trim() } })
      .then((response) => {
        const data = response.data.sendTestEmail;
        setResult(data);
        if (data.success) {
          toast.success(data.message, toastOptions);
        } else {
          toast.error(data.message, toastOptions);
        }
      })
      .catch((error) => {
        const message = `A problem occurred sending the test email: ${error.message}`;
        setResult({ success: false, message: message });
        toast.error(message, toastOptions);
      });
  };

  return (
    <Container>
      <Typography variant="h5">Test Email</Typography>

      <Typography variant="h6" style={{ marginTop: "15px" }}>
        Preview Email Templates
      </Typography>
      <Paper sx={{ px: 2, py: 2 }} style={{ marginTop: "10px" }}>
        <Typography variant="body1" style={{ marginBottom: "15px" }}>
          Preview how each email template renders. Templates are shown with
          sample data.
        </Typography>
        <FormControl fullWidth>
          <InputLabel id="template-select-label">Template</InputLabel>
          <Select
            labelId="template-select-label"
            label="Template"
            value={selectedTemplate}
            disabled={templateNamesQuery.loading}
            onChange={(e) => onSelectTemplate(e.target.value)}
          >
            {templateNames.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {previewTemplateResult.loading && (
          <div style={{ marginTop: "15px" }}>
            <CircularProgress size={24} />
          </div>
        )}
        {!previewTemplateResult.loading && selectedTemplate && previewHtml && (
          <iframe
            title="Email template preview"
            srcDoc={previewHtml}
            sandbox=""
            style={{
              width: "100%",
              height: "600px",
              border: "1px solid #ccc",
              marginTop: "15px",
              backgroundColor: "#fff",
            }}
          />
        )}
      </Paper>

      <Typography variant="h6" style={{ marginTop: "25px" }}>
        Send Test Email
      </Typography>
      <Paper
        sx={{ px: 2, py: 2, "& .MuiTextField-root": { m: 1 } }}
        style={{ marginTop: "10px" }}
      >
        <Typography variant="body1" style={{ marginBottom: "10px" }}>
          Send a test email to verify that the application's email configuration
          is working. If left blank, the email is sent to your own address.
        </Typography>
        <TextField
          variant="outlined"
          color="primary"
          label="Recipient Email"
          type="email"
          value={toEmail}
          fullWidth
          onChange={(e) => setToEmail(e.target.value)}
        />
        <br />
        <Button
          variant="contained"
          color="primary"
          disabled={sendTestEmailResult.loading}
          onClick={onSend}
        >
          {sendTestEmailResult.loading && <CustomCircularProgress />}
          Send Test Email
        </Button>
        {result && (
          <Alert
            severity={result.success ? "success" : "error"}
            style={{ marginTop: "15px" }}
          >
            {result.message}
          </Alert>
        )}
      </Paper>
    </Container>
  );
};
